import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Plus, Pencil, Trash2, Loader2, Search, X, ChevronDown, ChevronUp, Network, Upload, Download } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import {
  Select, SelectTrigger, SelectContent, SelectValue, SelectItem,
} from '../ui/select';
import { Textarea } from '../ui/textarea';
import {
  siteNetworkService, type NetworkBulkUploadResult, parseTreatmentParams,
} from '../../../../../features/billValidation/services/siteNetworkService';
import type {
  BvSiteNetwork, BvSiteNetworkFormData, BvEventType,
  BvDaysMapping, BvTimesMapping, BvMonthsMapping, BvTimeType, BvTariffTreatment,
} from '../../../../../features/billValidation/types/billValidationTypes';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { VocabularyTab, type VocabField } from './VocabularyTab';
import {
  daysMappingService, timesMappingService, monthsMappingService,
  timeTypeService, treatmentService,
} from '../../../../../features/billValidation/services/vocabularyService';

// ─── Constants ────────────────────────────────────────────────────────────────

const EMPTY_FORM: BvSiteNetworkFormData = {
  tariff_code: '', tariff_name: '',
  distributor: '', description: '',
  name: '', channel: '', market_region: '', category: '',
  days_mapping_id: '', times_mapping_id: '', months_mapping_id: '',
  time_type_id: '', treatment_id: '',
  bucket_key: '', bill_category: '', demand_metric: '', interval_min: '',
  event_type: '', treatment_params: '', notes: '',
  open_to_new_applicants: true, price: '', price_type: '',
};

function networkToForm(n: BvSiteNetwork): BvSiteNetworkFormData {
  return {
    tariff_code:            n.tariff_code,
    tariff_name:            n.tariff_name ?? '',
    distributor:            n.distributor ?? '',
    description:            n.description ?? '',
    name:                   n.name ?? '',
    channel:                n.channel ?? '',
    market_region:          n.market_region ?? '',
    category:               n.category ?? '',
    days_mapping_id:        n.days_mapping_id ?? '',
    times_mapping_id:       n.times_mapping_id ?? '',
    months_mapping_id:      n.months_mapping_id ?? '',
    time_type_id:           n.time_type_id ?? '',
    treatment_id:           n.treatment_id ?? '',
    bucket_key:             n.bucket_key ?? '',
    bill_category:          n.bill_category ?? '',
    demand_metric:          n.demand_metric ?? '',
    interval_min:           n.interval_min != null ? String(n.interval_min) : '',
    event_type:             n.event_type ?? '',
    treatment_params:       n.treatment_params ? JSON.stringify(n.treatment_params) : '',
    notes:                  n.notes ?? '',
    open_to_new_applicants: n.open_to_new_applicants ?? true,
    price:                  n.price != null ? String(n.price) : '',
    price_type:             n.price_type ?? '',
  };
}

// ─── Field groups ─────────────────────────────────────────────────────────────

type FieldDef = { key: keyof BvSiteNetworkFormData; label: string; type?: string; required?: boolean; span?: boolean; hint?: string };

const BASIC_FIELDS: FieldDef[] = [
  { key: 'tariff_code', label: 'Tariff Code', required: true },
  { key: 'tariff_name', label: 'Tariff Name' },
  { key: 'distributor', label: 'Distributor', required: true },
  { key: 'description', label: 'Description', span: true },
];

const BUCKET_FIELDS: FieldDef[] = [
  { key: 'name',           label: 'Name (bucket label)' },
  { key: 'channel',        label: 'Channel (E/B/K/Q/days)' },
  { key: 'market_region',  label: 'Market Region' },
  { key: 'category',       label: 'Category' },
  { key: 'price',          label: 'Price', type: 'number' },
  { key: 'price_type',     label: 'Price Type (unit)' },
  { key: 'bucket_key',     label: 'Bucket Key', hint: 'e.g. PEAK_USAGE — the structural join key' },
  { key: 'bill_category',  label: 'Bill Category (retailer wording)' },
  { key: 'demand_metric',  label: 'Demand Metric (kW/kVA)' },
  { key: 'interval_min',   label: 'Interval (minutes)', type: 'number' },
  { key: 'event_type',     label: 'Event Type',
    hint: 'Set only for event-driven buckets — the bucket then applies ONLY inside a matching bv_events window' },
  { key: 'treatment_params', label: 'Treatment Params (JSON)', span: true,
    hint: 'Overrides the treatment\'s defaults, e.g. {"threshold_source":"site_reliability","band_width_kw":5000}' },
  { key: 'notes',          label: 'Notes', span: true },
];

const EVENT_TYPE_OPTIONS: Array<{ value: BvEventType; label: string }> = [
  { value: 'CRITICAL_PEAK',     label: 'Critical Peak' },
  { value: 'CRITICAL_MINIMUM',  label: 'Critical Minimum' },
  { value: 'ENERGY_REWARD',    label: 'Energy Reward' },
  { value: 'DEMAND_RESPONSE',  label: 'Demand Response' },
];

const DAYS_FIELDS: Array<VocabField<BvDaysMapping>> = [
  { key: 'code', label: 'Code', type: 'text', required: true, hint: 'e.g. WORKING_DAYS' },
  { key: 'label', label: 'Label', type: 'text', required: true },
  { key: 'weekdays', label: 'Weekdays', type: 'intArray', required: true,
    hint: 'Comma-separated, Mon=0 … Sun=6. e.g. 0, 1, 2, 3, 4' },
  { key: 'excluded_holiday_classes', label: 'Excluded holiday classes', type: 'stringArray',
    hint: 'gazetted_full_day, bank, regional, local, part_day. Empty means holidays are ignored.' },
  { key: 'description', label: 'Description', type: 'text' },
  { key: 'sort_order', label: 'Sort', type: 'number' },
];

const TIMES_FIELDS: Array<VocabField<BvTimesMapping>> = [
  { key: 'code', label: 'Code', type: 'text', required: true, hint: 'e.g. W_1500_2100' },
  { key: 'label', label: 'Label', type: 'text', required: true, hint: 'e.g. 3pm-9pm' },
  { key: 'kind', label: 'Kind', required: true, type: { select: [
    { value: 'window',   label: 'Window — a clock range' },
    { value: 'all',      label: 'All — matches every slot' },
    { value: 'residual', label: 'Residual — everything not claimed by another row' },
    { value: 'none',     label: 'None — not time-based (e.g. daily supply)' },
  ] } },
  // start_hour / end_hour are GENERATED from windows[0] since migration 113 and
  // can no longer be written — the window list is the editable field.
  { key: 'windows', label: 'Windows', type: 'jsonArray', default: [],
    hint: 'JSON list, e.g. [{"start":12,"end":14},{"start":18,"end":20}]. '
        + 'Decimal hours, end EXCLUSIVE (10pm is 22, not 21.59). '
        + 'start ≥ end crosses midnight. A slot matches if it falls in ANY window. '
        + 'Leave [] unless Kind is Window.' },
  { key: 'sort_order', label: 'Sort', type: 'number' },
];

const MONTHS_FIELDS: Array<VocabField<BvMonthsMapping>> = [
  { key: 'code', label: 'Code', type: 'text', required: true },
  { key: 'label', label: 'Label', type: 'text', required: true },
  { key: 'months', label: 'Months', type: 'intArray', required: true,
    hint: 'Comma-separated 1-12. e.g. 6, 7, 8, 11, 12, 1, 2, 3' },
  { key: 'sort_order', label: 'Sort', type: 'number' },
];

const TIME_TYPE_FIELDS: Array<VocabField<BvTimeType>> = [
  { key: 'code', label: 'Code', type: 'text', required: true },
  { key: 'label', label: 'Label', type: 'text', required: true },
  { key: 'iana_tz', label: 'IANA timezone', type: 'text',
    hint: 'e.g. Australia/Sydney. Leave blank to resolve from the site state.' },
  { key: 'observes_dst', label: 'Observes DST', type: 'boolean' },
  { key: 'description', label: 'Description', type: 'text' },
];

const TREATMENT_FIELDS: Array<VocabField<BvTariffTreatment>> = [
  { key: 'code', label: 'Code', type: 'text', required: true },
  { key: 'label', label: 'Label', type: 'text', required: true },
  { key: 'function_name', label: 'Function', type: 'text', required: true,
    hint: 'Must be an existing bv_treat_* database function.' },
  { key: 'params', label: 'Params (JSON)', type: 'json',
    hint: 'e.g. {"interval_min": 30, "lookback_months": 12}' },
  { key: 'applicable_duration', label: 'Applicable duration', type: 'text',
    hint: 'e.g. Billing Month, 12 months' },
  { key: 'notes', label: 'Notes', type: 'text' },
];

// ─── Collapsible section ──────────────────────────────────────────────────────

function CollapsibleSection({
  title, open, onToggle, children,
}: { title: string; open: boolean; onToggle: () => void; children: React.ReactNode }) {
  return (
    <div className="border rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-2.5 bg-muted/40 text-sm font-medium text-foreground hover:bg-muted/60 transition-colors"
      >
        {title}
        {open ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
      </button>
      {open && <div className="p-4">{children}</div>}
    </div>
  );
}

// ─── Create / Edit dialog ─────────────────────────────────────────────────────

type TariffDialogProps = {
  open: boolean;
  editing: BvSiteNetwork | null;
  onClose: () => void;
  onSaved: (network: BvSiteNetwork) => void;
};

function TariffDialog({ open, editing, onClose, onSaved }: TariffDialogProps) {
  const [form, setForm] = useState<BvSiteNetworkFormData>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showBucket, setShowBucket] = useState(true);
  const [treatmentParamsError, setTreatmentParamsError] = useState<string | null>(null);
  const [vocab, setVocab] = useState<{
    days: BvDaysMapping[]; times: BvTimesMapping[]; months: BvMonthsMapping[];
    timeTypes: BvTimeType[]; treatments: BvTariffTreatment[];
  }>({ days: [], times: [], months: [], timeTypes: [], treatments: [] });

  useEffect(() => {
    if (open) {
      setForm(editing ? networkToForm(editing) : EMPTY_FORM);
      setError(null);
      setShowBucket(true);
      setTreatmentParamsError(null);
    }
  }, [open, editing]);

  useEffect(() => {
    if (!open) return;
    void (async () => {
      const [d, t, m, tt, tr] = await Promise.all([
        daysMappingService.list(), timesMappingService.list(), monthsMappingService.list(),
        timeTypeService.list(), treatmentService.list(),
      ]);
      setVocab({
        days: d.data ?? [], times: t.data ?? [], months: m.data ?? [],
        timeTypes: tt.data ?? [], treatments: tr.data ?? [],
      });
    })();
  }, [open]);

  const set = (key: keyof BvSiteNetworkFormData, val: string) =>
    setForm(prev => ({ ...prev, [key]: val }));

  const handleSave = async () => {
    if (!form.tariff_code.trim()) { setError('Tariff code is required'); return; }
    if (!form.distributor.trim()) { setError('Distributor is required'); return; }
    // Validate treatment params inline so the error lands on the field, not the
    // top-of-dialog banner — the user needs to see WHICH field rejected the save.
    setTreatmentParamsError(null);
    try {
      parseTreatmentParams(form.treatment_params);
    } catch (e) {
      setTreatmentParamsError((e as Error).message);
      return;
    }
    setSaving(true);
    setError(null);
    const res = editing
      ? await siteNetworkService.update(editing.id, form)
      : await siteNetworkService.create(form);
    setSaving(false);
    if (!res.success || !res.data) { setError(res.error ?? 'Save failed'); return; }
    onSaved(res.data);
  };

  const renderFields = (fields: FieldDef[]) =>
    fields.map(({ key, label, type, required, span, hint }) => {
      if (key === 'event_type') {
        return (
          <div key={key} className={span ? 'col-span-2' : ''}>
            <Label className="text-xs text-muted-foreground mb-1.5 block">
              {label}{required && <span className="text-destructive ml-0.5">*</span>}
            </Label>
            <Select
              value={form.event_type || '__none__'}
              onValueChange={v => set('event_type', v === '__none__' ? '' : v)}
            >
              <SelectTrigger className="h-9"><SelectValue placeholder="Not set" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Not set</SelectItem>
                {EVENT_TYPE_OPTIONS.map(o => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
          </div>
        );
      }
      if (key === 'treatment_params') {
        return (
          <div key={key} className={span ? 'col-span-2' : ''}>
            <Label className="text-xs text-muted-foreground mb-1.5 block">
              {label}{required && <span className="text-destructive ml-0.5">*</span>}
            </Label>
            <Textarea
              value={String(form.treatment_params ?? '')}
              onChange={e => { set('treatment_params', e.target.value); setTreatmentParamsError(null); }}
              className="font-mono text-xs"
              rows={3}
            />
            {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
            {treatmentParamsError && (
              <p className="text-xs text-destructive mt-1">{treatmentParamsError}</p>
            )}
          </div>
        );
      }
      return (
        <div key={key} className={span ? 'col-span-2' : ''}>
          <Label className="text-xs text-muted-foreground mb-1.5 block">
            {label}{required && <span className="text-destructive ml-0.5">*</span>}
          </Label>
          <Input
            type={type ?? 'text'}
            value={String(form[key] ?? '')}
            onChange={e => set(key, e.target.value)}
            className="h-9"
            step={type === 'number' ? 'any' : undefined}
          />
          {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
        </div>
      );
    });

  const vocabSelect = (
    label: string,
    field: 'days_mapping_id' | 'times_mapping_id' | 'months_mapping_id' | 'time_type_id' | 'treatment_id',
    options: Array<{ id: string; code: string; label: string }>,
    hint?: string,
  ) => (
    <div className="space-y-1">
      <Label>{label}</Label>
      <Select
        value={form[field] || '__none__'}
        onValueChange={v => setForm(s => ({ ...s, [field]: v === '__none__' ? '' : v }))}
      >
        <SelectTrigger><SelectValue placeholder="Not set" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__">Not set</SelectItem>
          {options.map(o => (
            <SelectItem key={o.id} value={o.id}>{o.label} ({o.code})</SelectItem>
          ))}
        </SelectContent>
      </Select>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editing ? `Edit — ${editing.tariff_code}` : 'New Network Tariff Rate'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div className="grid grid-cols-2 gap-4">
            {renderFields(BASIC_FIELDS)}
          </div>

          <CollapsibleSection title="Bucket" open={showBucket} onToggle={() => setShowBucket(v => !v)}>
            <div className="grid grid-cols-2 gap-4">
              {renderFields(BUCKET_FIELDS)}
            </div>

            <div className="grid grid-cols-2 gap-3 mt-4">
              {vocabSelect('Days', 'days_mapping_id', vocab.days,
                'Which weekdays, and which public holidays are excluded')}
              {vocabSelect('Times', 'times_mapping_id', vocab.times,
                '"All other times" is the catch-all for this tariff and channel')}
              {vocabSelect('Months', 'months_mapping_id', vocab.months)}
              {vocabSelect('Time Type', 'time_type_id', vocab.timeTypes,
                'Recorded for reference — no timezone conversion is applied yet')}
              {vocabSelect('Treatment', 'treatment_id', vocab.treatments,
                'Demand rows only — how the charged demand figure is derived')}
            </div>

            <div className="col-span-2 flex items-center gap-3 mt-4">
              <button
                type="button"
                role="switch"
                aria-checked={form.open_to_new_applicants}
                onClick={() => setForm((f) => ({ ...f, open_to_new_applicants: !f.open_to_new_applicants }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form.open_to_new_applicants ? 'bg-primary' : 'bg-muted'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${form.open_to_new_applicants ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
              <span className="text-sm text-muted-foreground">
                {form.open_to_new_applicants ? 'Open to new applicants' : 'Closed to new applicants'}
              </span>
            </div>
          </CollapsibleSection>

          {error && (
            <p className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded px-3 py-2">
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={() => void handleSave()} disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {saving ? 'Saving…' : editing ? 'Save Changes' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const NetworkTariffsManagement: React.FC = () => {
  const [networks, setNetworks] = useState<BvSiteNetwork[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const [vocab, setVocab] = useState<{
    days: BvDaysMapping[]; times: BvTimesMapping[]; months: BvMonthsMapping[];
    timeTypes: BvTimeType[]; treatments: BvTariffTreatment[];
  }>({ days: [], times: [], months: [], timeTypes: [], treatments: [] });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingNetwork, setEditingNetwork] = useState<BvSiteNetwork | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState<File | null>(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState<NetworkBulkUploadResult | null>(null);
  const bulkFileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await siteNetworkService.listAll();
    if (res.success) setNetworks(res.data ?? []);
    else setError(res.error ?? 'Failed to load');
    setLoading(false);
  }, []);

  useEffect(() => { void load(); }, [load]);

  // ponytail: vocab loads once; rates table resolves FK ids to labels via labelOf.
  useEffect(() => {
    void (async () => {
      const [d, t, m, tt, tr] = await Promise.all([
        daysMappingService.list(), timesMappingService.list(), monthsMappingService.list(),
        timeTypeService.list(), treatmentService.list(),
      ]);
      setVocab({
        days: d.data ?? [], times: t.data ?? [], months: m.data ?? [],
        timeTypes: tt.data ?? [], treatments: tr.data ?? [],
      });
    })();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return networks;
    return networks.filter(n =>
      n.tariff_code.toLowerCase().includes(q) ||
      (n.tariff_name ?? '').toLowerCase().includes(q) ||
      (n.distributor ?? '').toLowerCase().includes(q)
    );
  }, [networks, search]);

  const handleSaved = (saved: BvSiteNetwork) => {
    setNetworks(prev => {
      const idx = prev.findIndex(n => n.id === saved.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = saved;
        return next.sort((a, b) => a.tariff_code.localeCompare(b.tariff_code));
      }
      return [...prev, saved].sort((a, b) => a.tariff_code.localeCompare(b.tariff_code));
    });
    setDialogOpen(false);
    setEditingNetwork(null);
  };

  const handleDelete = async (id: string) => {
    setDeleting(true);
    const res = await siteNetworkService.delete(id);
    if (res.success) setNetworks(prev => prev.filter(n => n.id !== id));
    setDeleting(false);
    setDeleteConfirmId(null);
  };

  const openCreate = () => { setEditingNetwork(null); setDialogOpen(true); };
  const openEdit   = (n: BvSiteNetwork) => { setEditingNetwork(n); setDialogOpen(true); };

  const downloadSample = () => {
    const csv = [
      'tariff_code,tariff_name,distributor,description,name,channel,market_region,holiday_names,category,start_time,end_time,weekdays,months_included,open_to_new_applicants,price,price_type',
      'LVS1R,Residential Flat,United Energy,General Supply,Supply,days,AU-NEM.VIC,,Fixed,,,0|1|2|3|4|5|6,,true,0.3151,$/day',
      "URSTOU,Residential TOU,United Energy,TOU tariff,Peak Usage,E,AU-NEM.VIC,,Usage,16:00,21:00,0|1|2|3|4|5|6,,true,20.92,c/kWh",
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'network_tariffs_sample.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const openBulkDialog = () => { setBulkFile(null); setBulkResult(null); setBulkDialogOpen(true); };

  const handleBulkFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBulkFile(e.target.files?.[0] ?? null);
    setBulkResult(null);
  };

  const handleBulkUpload = async () => {
    if (!bulkFile) return;
    setBulkUploading(true);
    const result = await siteNetworkService.bulkUpload(bulkFile);
    setBulkResult(result);
    setBulkUploading(false);
    if (result.success && result.inserted > 0) void load();
  };

  const labelOf = (list: Array<{ id: string; label: string }>, id: string | null) =>
    (id ? list.find(x => x.id === id)?.label : null) ?? '—';
  const em = () => <span className="text-muted-foreground/40">—</span>;

  return (
    <Tabs defaultValue="rates" className="space-y-4">
      <TabsList>
        <TabsTrigger value="rates">Rates</TabsTrigger>
        <TabsTrigger value="days">Days</TabsTrigger>
        <TabsTrigger value="times">Times</TabsTrigger>
        <TabsTrigger value="months">Months</TabsTrigger>
        <TabsTrigger value="timeTypes">Time Types</TabsTrigger>
        <TabsTrigger value="treatments">Treatments</TabsTrigger>
      </TabsList>

      <TabsContent value="rates">
      <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Network Tariffs</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Bucket-level distributor tariff rates — one row per (distributor, tariff code, bucket). Windowed by time, weekdays, months and holidays.
          </p>
        </div>
        <Button variant="outline" onClick={openBulkDialog} className="gap-2">
          <Upload className="h-4 w-4" />
          Bulk upload
        </Button>
        <Button onClick={openCreate} className="gap-2">
          <Plus className="h-4 w-4" />
          New Code
        </Button>
      </div>

      {/* Search + count */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by code, name or distributor…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9 pr-8"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        {!loading && (
          <span className="text-sm text-muted-foreground tabular-nums">
            {filtered.length} of {networks.length}
          </span>
        )}
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16 gap-2 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-sm">Loading tariff codes…</span>
            </div>
          ) : error ? (
            <div className="py-16 text-center">
              <p className="text-sm text-destructive">{error}</p>
              <Button variant="outline" size="sm" className="mt-3" onClick={() => void load()}>
                Retry
              </Button>
            </div>
          ) : networks.length === 0 ? (
            <div className="py-16 text-center text-muted-foreground">
              <Network className="h-10 w-10 mx-auto mb-3 opacity-20" />
              <p className="text-sm font-medium">No tariff codes defined</p>
              <p className="text-xs mt-1">Click "New Code" to add the first one</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center text-muted-foreground">
              <p className="text-sm">No results for "{search}"</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="whitespace-nowrap sticky left-0 bg-background z-10 shadow-[2px_0_6px_-1px_rgba(0,0,0,0.12)]">Tariff Code</TableHead>
                  <TableHead className="whitespace-nowrap">Tariff Name</TableHead>
                  <TableHead className="whitespace-nowrap">Distributor</TableHead>
                  <TableHead className="whitespace-nowrap">Name</TableHead>
                  <TableHead className="whitespace-nowrap">Channel</TableHead>
                  <TableHead className="whitespace-nowrap">Market Region</TableHead>
                  <TableHead className="whitespace-nowrap">Category</TableHead>
                  <TableHead className="whitespace-nowrap">Days</TableHead>
                  <TableHead className="whitespace-nowrap">Times</TableHead>
                  <TableHead className="whitespace-nowrap">Months</TableHead>
                  <TableHead className="whitespace-nowrap">Treatment</TableHead>
                  <TableHead className="whitespace-nowrap text-right">Price</TableHead>
                  <TableHead className="whitespace-nowrap">Unit</TableHead>
                  <TableHead className="whitespace-nowrap">Open to new</TableHead>
                  <TableHead className="whitespace-nowrap w-24 sticky right-0 bg-background z-10 shadow-[-2px_0_6px_-1px_rgba(0,0,0,0.12)]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(n => (
                  <TableRow key={n.id}>
                    <TableCell className="font-mono font-semibold whitespace-nowrap sticky left-0 bg-background z-10 shadow-[2px_0_6px_-1px_rgba(0,0,0,0.12)]">{n.tariff_code}</TableCell>
                    <TableCell className="whitespace-nowrap text-sm">{n.tariff_name ?? em()}</TableCell>
                    <TableCell className="whitespace-nowrap text-sm">{n.distributor ?? em()}</TableCell>
                    <TableCell className="whitespace-nowrap text-sm">{n.name ?? em()}</TableCell>
                    <TableCell className="whitespace-nowrap text-sm">{n.channel ?? em()}</TableCell>
                    <TableCell className="whitespace-nowrap text-sm">{n.market_region ?? em()}</TableCell>
                    <TableCell className="whitespace-nowrap text-sm">{n.category ?? em()}</TableCell>
                    <TableCell className="text-sm whitespace-nowrap">{labelOf(vocab.days, n.days_mapping_id)}</TableCell>
                    <TableCell className="text-sm whitespace-nowrap">{labelOf(vocab.times, n.times_mapping_id)}</TableCell>
                    <TableCell className="text-sm whitespace-nowrap">{labelOf(vocab.months, n.months_mapping_id)}</TableCell>
                    <TableCell className="text-sm whitespace-nowrap">{labelOf(vocab.treatments, n.treatment_id)}</TableCell>
                    <TableCell className="text-right tabular-nums text-sm">{n.price ?? em()}</TableCell>
                    <TableCell className="whitespace-nowrap text-sm">{n.price_type ?? em()}</TableCell>
                    <TableCell className="text-sm">{n.open_to_new_applicants === false ? 'No' : 'Yes'}</TableCell>
                    <TableCell className="sticky right-0 bg-background z-10 shadow-[-2px_0_6px_-1px_rgba(0,0,0,0.12)]">
                      <div className="flex items-center justify-end gap-1">
                        {deleteConfirmId === n.id ? (
                          <>
                            <span className="text-xs text-destructive mr-1">Delete?</span>
                            <Button
                              variant="destructive" size="sm" className="h-7 px-2 text-xs"
                              onClick={() => void handleDelete(n.id)}
                              disabled={deleting}
                            >
                              {deleting ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Yes'}
                            </Button>
                            <Button
                              variant="outline" size="sm" className="h-7 px-2 text-xs"
                              onClick={() => setDeleteConfirmId(null)}
                              disabled={deleting}
                            >
                              No
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              variant="ghost" size="sm" className="h-8 w-8 p-0"
                              onClick={() => openEdit(n)}
                              title="Edit"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost" size="sm" className="h-8 w-8 p-0 hover:text-destructive"
                              onClick={() => setDeleteConfirmId(n.id)}
                              title="Delete"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bulk upload dialog */}
      <Dialog open={bulkDialogOpen} onOpenChange={(open) => { if (!bulkUploading) setBulkDialogOpen(open); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Bulk Upload Network Tariffs</DialogTitle>
            <p className="text-sm text-muted-foreground pt-1">
              Upload CSV or XLSX. Required: <strong>tariff_code</strong>, <strong>distributor</strong>. Weekdays as pipe-separated integers (0=Mon … 6=Sun). Months/holiday names as pipe-separated values, e.g. <code>Dec|Jan|Feb</code> — empty means "all".
            </p>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <button type="button" onClick={downloadSample} className="flex items-center gap-1.5 text-sm text-primary hover:underline">
              <Download className="h-4 w-4" /> Download sample CSV
            </button>

            <div
              className="flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border p-6 cursor-pointer hover:bg-muted/40 transition-colors"
              onClick={() => bulkFileRef.current?.click()}
            >
              <Upload className="h-8 w-8 text-muted-foreground" />
              {bulkFile ? (
                <span className="text-sm font-medium">{bulkFile.name}</span>
              ) : (
                <span className="text-sm text-muted-foreground">Click to select CSV or XLSX file</span>
              )}
              <input ref={bulkFileRef} type="file" accept=".csv,.xlsx" className="hidden" onChange={handleBulkFileChange} />
            </div>

            {bulkResult && (
              <div className="space-y-2">
                {bulkResult.success ? (
                  <p className="text-sm bg-muted rounded p-3">
                    <span className="font-medium">Upload complete</span>
                    {' — '}{bulkResult.inserted} inserted · {bulkResult.errors.length} errors (of {bulkResult.total} rows)
                  </p>
                ) : (
                  <p className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded px-3 py-2">
                    {bulkResult.error}
                  </p>
                )}
                {bulkResult.errors.length > 0 && (
                  <div className="max-h-36 overflow-y-auto rounded-md border bg-muted/30 p-2 text-xs space-y-1">
                    {bulkResult.errors.map((e) => <div key={e.row} className="text-destructive">Row {e.row}: {e.error}</div>)}
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDialogOpen(false)} disabled={bulkUploading}>
              {bulkResult?.success ? 'Close' : 'Cancel'}
            </Button>
            {!bulkResult?.success && (
              <Button onClick={() => void handleBulkUpload()} disabled={!bulkFile || bulkUploading}>
                {bulkUploading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {bulkUploading ? 'Uploading…' : 'Upload'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <TariffDialog
        open={dialogOpen}
        editing={editingNetwork}
        onClose={() => { setDialogOpen(false); setEditingNetwork(null); }}
        onSaved={handleSaved}
      />
    </div>
      </TabsContent>

      <TabsContent value="days">
        <VocabularyTab title="Days Mapping" service={daysMappingService} fields={DAYS_FIELDS}
          columns={['code', 'label', 'weekdays', 'excluded_holiday_classes']} />
      </TabsContent>
      <TabsContent value="times">
        <VocabularyTab title="Times Mapping" service={timesMappingService} fields={TIMES_FIELDS}
          columns={['code', 'label', 'kind', 'windows']} />
      </TabsContent>
      <TabsContent value="months">
        <VocabularyTab title="Months Mapping" service={monthsMappingService} fields={MONTHS_FIELDS}
          columns={['code', 'label', 'months']} />
      </TabsContent>
      <TabsContent value="timeTypes">
        <VocabularyTab title="Time Type" service={timeTypeService} fields={TIME_TYPE_FIELDS}
          columns={['code', 'label', 'iana_tz', 'observes_dst']} />
      </TabsContent>
      <TabsContent value="treatments">
        <VocabularyTab title="Tariff Treatment" service={treatmentService} fields={TREATMENT_FIELDS}
          columns={['code', 'label', 'function_name', 'applicable_duration']} />
      </TabsContent>
    </Tabs>
  );
};

export default NetworkTariffsManagement;
